/*
  Clive: mobile drawer navigation — expand in place instead of sliding panels.

  Stiletto's drawer is a drill-down: every sub-list is `position:absolute; left:100%`
  and its JS slides the whole container one panel per level. That put any collection
  four taps away (hamburger -> Shop -> group -> link) and swapped the drawer's chrome
  on the way in — the logo, search and cart are replaced by a bare back arrow.

  This turns it into: SHOP open at rest with its groups and links inline, LEARN and
  SUPPORT as collapsed accordions. Two taps to a collection, three to an article, and
  the header never changes because no second panel is ever shown.

  Approach: the sub-lists are ALREADY rendered inline in the DOM by
  snippets/drawer-menu-list.liquid — they are only positioned off-screen. So this is a
  behaviour + CSS change, not a markup rebuild. The matching CSS lives in
  assets/custom.css under "Mobile drawer".

  The click handler binds in the CAPTURE phase on `document` so it runs before
  Stiletto's own delegated listener regardless of binding order, and stops that
  listener from starting a panel slide. Same technique as the PDP add-ons handler.

  Scope: ONLY the primary links list. The country and language selectors reuse
  `.drawer-menu__list--sub` and must keep Stiletto's native behaviour untouched.
*/
(function () {
  'use strict';

  var PRIMARY = '[data-primary-container="true"]';
  var OPEN = 'clive-nav-open';
  var DEFAULT_OPEN = 'shop'; // handle of the top-level item open at rest

  function primaryList(el) {
    return el && el.closest ? el.closest(PRIMARY) : null;
  }

  /* The <li> that owns a given parent trigger. */
  function itemFor(trigger) {
    return trigger.closest('[data-list-item]');
  }

  function isTopLevel(li) {
    var list = li.parentElement;
    return !!list && list.matches(PRIMARY);
  }

  function setOpen(li, open) {
    li.classList.toggle(OPEN, open);

    var trigger = li.querySelector(':scope > [data-item="parent"]');
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');

    var sub = li.querySelector(':scope > .drawer-menu__list--sub');
    if (!sub) return;
    sub.setAttribute('aria-hidden', open ? 'false' : 'true');

    // Height is driven inline, not by a CSS class rule. scrollHeight is the list's
    // real content height, so the transition lands exactly and no ceiling constant
    // can be outgrown when the menu changes in admin.
    //
    // scrollHeight reads 0 while an ancestor is display:none — which is exactly the
    // case at desktop widths, where the drawer is hidden but this still runs on load.
    // Fall back to a ceiling then, so a section can never be stuck closed at 0.
    sub.style.maxHeight = open ? (sub.scrollHeight || 3000) + 'px' : '0px';
  }

  /* Only one top-level section open at a time; nested groups toggle freely. */
  function toggle(li) {
    var opening = !li.classList.contains(OPEN);
    if (opening && isTopLevel(li)) {
      var list = li.parentElement;
      Array.prototype.forEach.call(list.children, function (sibling) {
        if (sibling !== li && sibling.classList.contains(OPEN)) setOpen(sibling, false);
      });
    }
    setOpen(li, opening);
  }

  document.addEventListener(
    'click',
    function (event) {
      var trigger = event.target.closest && event.target.closest('[data-item="parent"]');
      if (!trigger) return;
      if (!primaryList(trigger)) return; // locale/currency selectors keep native behaviour

      var li = itemFor(trigger);
      if (!li) return;

      // Stop Stiletto's delegated handler from starting a panel slide either way.
      event.stopImmediatePropagation();

      if (!isTopLevel(li)) {
        // A group row like "Start Here" is a real collection link and its children
        // are already visible below it, so let the navigation happen.
        return;
      }

      event.preventDefault();
      toggle(li);
    },
    true
  );

  /* Open the default section once the drawer markup exists. */
  function applyDefault(root) {
    var list = root.querySelector(PRIMARY);
    if (!list || list.dataset.cliveNavInit === 'true') return;
    list.dataset.cliveNavInit = 'true';

    Array.prototype.forEach.call(list.children, function (li) {
      var link = li.querySelector(':scope > [data-item="parent"]');
      if (!link) return;
      var label = (link.textContent || '').trim().toLowerCase();
      setOpen(li, label.indexOf(DEFAULT_OPEN) === 0);
    });
  }

  function boot() {
    document.querySelectorAll('[data-drawer-menu]').forEach(applyDefault);
  }

  /* Tapping Call or Text hands the user off to the dialler or messages app. The
     drawer panel is a full-screen WHITE overlay (position:absolute, 100vw/100vh,
     z-index:100, background = header background), so if it is still open when they
     come back to the browser they see a blank white page with only the floating
     widgets on it — which is exactly what was reported.

     So close the drawer as the handoff starts. The click is NOT prevented: the
     tel:/sms: navigation still happens, and closing runs on the next tick so it
     cannot interfere with it. */
  document.addEventListener(
    'click',
    function (event) {
      var action = event.target.closest && event.target.closest('.clive-drawer-contact__action');
      if (!action) return;
      setTimeout(function () {
        var toggle = document.querySelector('.header__icon-menu');
        var panel = document.querySelector('.drawer-menu__panel');
        if (!toggle || !panel) return;
        if (getComputedStyle(panel).visibility === 'visible') toggle.click();
      }, 0);
    },
    false
  );

  /* Opening the drawer is the first moment the lists have a real height, so
     re-apply the open section's height then. */
  document.addEventListener(
    'click',
    function (event) {
      var toggleBtn = event.target.closest && event.target.closest('.header__icon-menu');
      if (!toggleBtn) return;
      setTimeout(function () {
        document.querySelectorAll('[data-primary-container="true"] > .' + OPEN).forEach(function (li) {
          var sub = li.querySelector(':scope > .drawer-menu__list--sub');
          if (sub) sub.style.maxHeight = (sub.scrollHeight || 3000) + 'px';
        });
      }, 60);
    },
    false
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // The header re-renders as a section in the theme editor.
  document.addEventListener('shopify:section:load', boot);
})();
